import Vue from 'vue';
import * as env from '~/server_env';
import ignoreErrors from './ignoreErrors';
import { formatComponentName, generateComponentTrace } from '@sentry/vue/esm/vendor/components';

export const Severity = {
  Critical: 'critical',
  Fatal: 'fatal',
  Error: 'error',
  Warning: 'warning',
  Info: 'info',
  Log: 'log',
  Debug: 'debug',
};

/**
 * Wrapper around Sentry itself that will allow us to lazy load Sentry when the first
 * error takes place. We won't lose any errors since we're adding them into a queue
 * to be processed later when Sentry has been attached.
 */
export default class HubstaffErrorReporter {
  queuedErrors = [];
  loading = undefined; // undefined, true, false

  // @type {import('@sentry/browser')}
  sentry = null;

  constructor() {
    // Force `reportError` to always point to the instance!!!
    // We need this because we're attaching us to the `window` as a global helper
    // and `this` will not point correctly otherwise.
    this.reportError = this.reportError.bind(this);

    // Manually set error handling at the global scope so that we can then
    // lazy load sentry when the first error occurs.
    window.onerror = this.reportError;
    this.#attachVueErrorHandler();
  }

  async load() {
    try {
      this.loading = true;
      const [Sentry, { BrowserTracing }] = await Promise.all([import('@sentry/browser'), import('@sentry/tracing')]);

      this.sentry = Sentry;
      this.loading = false;

      // Clear out the manual error handling from the window, Sentry
      // will add its hooks in order to handle exceptions.
      window.onerror = undefined;
      Vue.config.errorHandler = undefined;

      // Get the host for the app, make sure we only get a string
      // with the form of `app.hubstaff.com`.
      const url = new URL(env.APP_HOST);
      const host = url.host;
      let allowUrls = { allowUrls: [new RegExp(host)] };

      if (import.meta.env.DEV) {
        allowUrls = {};
      }

      this.sentry.init({
        debug: import.meta.env.DEV,
        dsn: env.SENTRY_DSN,
        integrations: [new BrowserTracing()],
        tracesSampleRate: env.SENTRY_TRACES_SAMPLE_RATE,
        sampleRate: env.SENTRY_SAMPLE_RATE,
        release: env.RELEASE_NAME,
        environment: env.SENTRY_ENVIRONMENT,
        autoSessionTracking: true,
        denyUrls: [/file:\//i, /^chrome:\/\//i, /extension:\/\//i],
        beforeSend(event, hint) {
          const status = hint?.originalException?.params?.status;
          
          if (status === 400 || status === 422) {
            return null; // drop, don't send
          }

          return event;
        },
        ...allowUrls,
        ignoreErrors,
      });

      this.#setUser();
      this.#processQueuedErrors();
    } catch (err) {
      console.error('Unable to load/init sentry', err);
    }
  }

  reportError(error, context = {}, severity = Severity.Error) {
    // `context` can be an empty string, which won't default to `{}`
    // in the default parameters initialization.
    context ||= {};
    severity = this.#getSeverity(severity);
    context = typeof context === 'string' ? { url: context } : context;

    if (this.loading === undefined) {
      // First hit...
      this.queuedErrors.push({ error, context, severity });
      this.load();
    } else if (this.loading === true) {
      // Hit if we try to report but we have not yet finished loading Sentry.
      this.queuedErrors.push({ error, context, severity });
    } else {
      // Everything is loaded, go ahead and delegate to `#doErrorReport`.
      this.#doErrorReport(error, context, severity);
    }
  }

  // =======
  // Private
  // =======

  #setUser() {
    let userDetails = {};

    if (document.body && document.body.dataset && document.body.dataset.currentUserDetails) {
      userDetails = JSON.parse(document.body.dataset.currentUserDetails);
    }

    this.sentry.setUser(userDetails);
  }

  #processQueuedErrors() {
    // Process any queued errors...
    this.queuedErrors.forEach(({ error, context, severity }) => {
      this.#doErrorReport(error, context, severity);
    });
  }

  #doErrorReport(error, context = {}, severity = Severity.Error) {
    // Capture exception in the next event loop, to make sure that all breadcrumbs are recorded in time.
    queueMicrotask(() => {
      this.sentry.withScope((scope) => {
        scope.setLevel(severity);

        Object.entries(context).forEach(([key, value]) => {
          scope.setContext(key, value);
        });

        this.sentry.captureException(error);
      });
    });
  }

  #getSeverity(severity) {
    return (
      {
        [Severity.Critical]: 'critical',
        [Severity.Fatal]: 'fatal',
        [Severity.Error]: 'error',
        [Severity.Warning]: 'warning',
        [Severity.Info]: 'info',
        [Severity.Log]: 'log',
        [Severity.Debug]: 'debug',
      }[severity] || 'error'
    );
  }

  #attachVueErrorHandler() {
    const { errorHandler, warnHandler, silent } = Vue.config;

    // The following is adapted from @sentry/vue/errorHandler
    Vue.config.errorHandler = (error, vm, lifecycleHook) => {
      const componentName = formatComponentName(vm, false);
      const trace = vm ? generateComponentTrace(vm) : '';
      const metadata = { componentName, lifecycleHook, trace };
      // Vue2 - $options.propsData
      // Vue3 - $props
      const propsData = vm && (vm.$options.propsData || vm.$props);

      if (propsData) {
        metadata.propsData = propsData;
      }

      this.reportError(error, metadata);

      // Call any existing errorHandler attached to the Vue config
      if (typeof errorHandler === 'function') {
        errorHandler.call(Vue, error, vm, lifecycleHook);
      }

      // Ensure we log the error
      const hasConsole = typeof console !== 'undefined';
      const message = `Error in ${lifecycleHook}: "${error && error.toString()}"`;

      if (typeof warnHandler === 'function') {
        warnHandler(message, vm, trace);
      } else if (hasConsole && !silent) {
        // eslint-disable-next-line no-console
        console.error(`[Vue warn]: ${message}${trace}`);
      }
    };
  }
}
